"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.remoteWorklogSchema = exports.remoteIssueSchema = exports.remoteRefSchema = exports.remoteFieldSchema = exports.remoteUserSchema = exports.remoteProjectSchema = void 0;
exports.metafieldTypesFor = metafieldTypesFor;
exports.isMappable = isMappable;
exports.assertMetafieldTypesAreKnown = assertMetafieldTypesAreKnown;
const zod_1 = require("zod");
const metafields_1 = require("../metafields/metafields");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C34 · What a remote tracker looks like, in OUR words.                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS FILE IS THE SEAM C40 DEPENDS ON, AND IT IS THE ONE THING C34 CANNOT GET WRONG
 * CHEAPLY.** `youtrack.md` §6.5 says it twice: the discovery adapter must return a
 * provider-agnostic shape *before* C34 writes Jira's, or C40 spends its budget adapting Jira's
 * client instead of implementing YouTrack's. Every shape below is deliberately the intersection of
 * what both trackers can answer — never the union, and never Jira's own field names.
 *
 * ⚠️ **`remoteType` is the exception, and it stays the provider's own word.** Jira says `option`
 * and `array` with `items`; YouTrack says `enum[1]` and `state[1]`, spaces and all. Paraphrasing
 * either into a shared vocabulary loses the distinction between `enum[1]` and `state[1]` that the
 * status map depends on (`youtrack.md` §6.3) — so it travels verbatim and only
 * `metafieldTypesFor()` below is allowed to interpret it.
 *
 * ⚠️ **Nothing here has a secret in it.** These are read results; a credential is `0060`'s
 * ciphertext column and `jira.md` §9.1's two-credential rule.
 */
/* ── the container ─────────────────────────────────────────────────────────────────────────── */
/**
 * A remote project — a **space** on Jira, a project on YouTrack (P2-23).
 *
 * ⚠️⚠️ **`remoteProjectId` IS THE IMMUTABLE NUMERIC/OPAQUE ID, NEVER THE KEY** (`jira.md` §8.4). A
 * Jira project key can be renamed and every issue key in it changes with it; a model keyed on `CEG`
 * detaches silently the day an admin tidies up, and every later read re-creates work we already
 * hold. The key is display, and it self-heals whenever a read disagrees.
 */
exports.remoteProjectSchema = zod_1.z.object({
    remoteProjectId: zod_1.z.string().min(1).max(200),
    key: zod_1.z.string().min(1).max(80),
    name: zod_1.z.string().min(1).max(400),
    /** ⚠️ Archived projects still answer reads. Shown, but not offered for linking. */
    archived: zod_1.z.boolean(),
});
/* ── a person ──────────────────────────────────────────────────────────────────────────────── */
/**
 * A remote account, as discovery found it.
 *
 * ⚠️ **`email` is nullable and is a matching HINT, never a key** (`jira.md` §1.6): Jira returns no
 * address for most users, while YouTrack usually does. §9.10's auto-match works around that by
 * *searching* on an address Jira will not show us — so the absence here is expected, not an error.
 */
exports.remoteUserSchema = zod_1.z.object({
    remoteUserId: zod_1.z.string().min(1).max(200),
    name: zod_1.z.string().max(400),
    email: zod_1.z.string().max(320).nullable(),
    /** ⚠️ A deactivated leaver is not who you want to pair somebody with (§9.10). */
    active: zod_1.z.boolean(),
});
/* ── a field we do not store ───────────────────────────────────────────────────────────────── */
/**
 * One discoverable field, for the wizard's `fields` step (P2-20).
 *
 * ⚠️ `options` is empty for everything that is not a choice. It is populated at discovery because
 * YouTrack returns it inline and Jira needs a context lookup per field — the cost difference lives
 * in the adapter, and the shape does not care.
 */
exports.remoteFieldSchema = zod_1.z.object({
    remoteFieldId: zod_1.z.string().min(1).max(200),
    name: zod_1.z.string().min(1).max(400),
    /** ⚠️ The provider's own word. The header says why it is not translated. */
    remoteType: zod_1.z.string().min(1).max(80),
    options: zod_1.z.array(zod_1.z.string().max(400)),
    /**
     * ⚠️ Whether a first-class column already consumes this field — status, assignee, summary. The
     * step greys these rather than hiding them, because *"why can't I map Status?"* deserves an
     * answer on the screen rather than a silence (§7.3).
     */
    claimed: zod_1.z.boolean(),
});
/* ── an issue ──────────────────────────────────────────────────────────────────────────────── */
/**
 * One remote issue, reduced to what we can act on.
 *
 * ⚠️⚠️ **THIS IS NOT A JIRA ISSUE AND MUST NOT GROW INTO ONE.** Anything a Jira response carries
 * that we have no column and no metafield for is dropped by the adapter, not carried here in case
 * it turns out useful — a shape that mirrors one provider is a shape the other cannot fill.
 *
 * ⚠️ Every name below is a REMOTE identifier: a status is its name (the map turns it into ours), an
 * assignee is a remote user id (an identity turns it into a member). The translation happens in the
 * apply service, so this stays true whatever a workspace has mapped.
 */
/** A remote vocabulary entry — a component or a fix version — by its id, with a name to show. */
exports.remoteRefSchema = zod_1.z.object({
    id: zod_1.z.string().min(1).max(200),
    name: zod_1.z.string().max(400),
});
exports.remoteIssueSchema = zod_1.z.object({
    remoteIssueId: zod_1.z.string().min(1).max(200),
    /** ⚠️ Display and self-healing, exactly like `RemoteProject.key`. */
    key: zod_1.z.string().min(1).max(80),
    remoteProjectId: zod_1.z.string().min(1).max(200),
    title: zod_1.z.string().max(1000),
    /** ⚠️ HTML, converted by the adapter. Jira speaks ADF and the conversion is lossy (§1.7). */
    descriptionHtml: zod_1.z.string().nullable(),
    statusName: zod_1.z.string().max(200).nullable(),
    priorityName: zod_1.z.string().max(200).nullable(),
    assigneeRemoteUserId: zod_1.z.string().max(200).nullable(),
    /** ⚠️ **Seconds, not story points, and never a guess** (§3.3). Null where the tracker has none. */
    originalEstimateSeconds: zod_1.z.number().int().nonnegative().nullable(),
    remainingEstimateSeconds: zod_1.z.number().int().nonnegative().nullable(),
    /** Calendar dates, both — `YYYY-MM-DD`, never an instant (`hard-won.md`). */
    startsOn: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/u)
        .nullable(),
    dueOn: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/u)
        .nullable(),
    labels: zod_1.z.array(zod_1.z.string().max(200)),
    parentRemoteIssueId: zod_1.z.string().max(200).nullable(),
    /**
     * ⚠️ **Jira's components and fix versions, as `{id, name}`** — `0160`. Rules compare the id,
     * because that is what survives a rename; the name is for the rule editor to show. A tracker with
     * no such notion sends two empty lists.
     */
    components: zod_1.z.array(exports.remoteRefSchema),
    fixVersions: zod_1.z.array(exports.remoteRefSchema),
    /**
     * ⚠️⚠️ **THE EPIC THIS ISSUE SITS UNDER DIRECTLY, AND ONLY DIRECTLY** — itself when it is an epic,
     * its parent when the parent is one, otherwise null. A subtask's epic is its PARENT's, which the
     * payload does not carry (Jira sends one level of parent), so the rule engine resolves it through
     * the parent's link rather than this field guessing.
     */
    epicRemoteIssueId: zod_1.z.string().max(200).nullable(),
    /**
     * ⚠️⚠️ **THE PROVIDER'S OWN LAST-MODIFIED, AND THE MONOTONIC GUARD DEPENDS ON IT** (§9.2). An ISO
     * instant as the tracker reports it. Retries arrive out of order, and applying an older payload
     * after a newer one reverts the issue to a state Jira left ten minutes ago.
     */
    remoteUpdatedAt: zod_1.z.string().min(1),
    /**
     * 30 Oct 2026 · ⚠️ **ADDED UNDER RULE 8 — when the issue was opened in the tracker.** An ISO
     * instant as the provider reports it. Kept on the link (`0247`) for the overview's burn charts —
     * a synced task's own creation is the day it was first synced. Optional, so a payload from
     * before it still parses.
     */
    remoteCreatedAt: zod_1.z.string().min(1).nullable().optional(),
    /**
     * The mapped extra fields, keyed by `remoteFieldId` (P2-20).
     *
     * ⚠️ **Only what somebody asked for.** The adapter requests exactly the ids in
     * `integration_field_maps` and puts back exactly those — an issue does not arrive carrying a
     * client's whole custom field schema on the chance we want it.
     */
    fields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
});
/* ── an hour ───────────────────────────────────────────────────────────────────────────────── */
/**
 * One remote worklog.
 *
 * ⚠️⚠️ **A DATE AND A TIME, NEVER AN INSTANT — and this is the conversion `hard-won.md` warns
 * about most.** Jira reports `started` as an instant (`.000+0000`, not `Z`); `worklogs` splits it
 * into `worked_on` and `started_at` because a calendar day and a moment are different things.
 * Composing or decomposing one needs a timezone, and **the adapter is the only place allowed to
 * choose it** — `members.timezone` where we know the person, the workspace's otherwise, and the
 * choice is recorded rather than assumed.
 */
exports.remoteWorklogSchema = zod_1.z.object({
    remoteWorklogId: zod_1.z.string().min(1).max(200),
    /** ⚠️ The issue's immutable id, never its key (§8.4). */
    remoteIssueId: zod_1.z.string().min(1).max(200),
    /** ⚠️ Null where the tracker withheld it — the hour is still real and still parks. */
    remoteAuthorId: zod_1.z.string().max(200).nullable(),
    workedOn: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
    /** `HH:MM`, or null where the tracker gave only a day. */
    startedAt: zod_1.z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/u)
        .nullable(),
    /** ⚠️ Hours, not seconds — converted once, at the boundary, with the rounding stated. */
    hours: zod_1.z.number().positive(),
    note: zod_1.z.string().nullable(),
    remoteUpdatedAt: zod_1.z.string().min(1),
});
/* ── where a remote field is allowed to land ───────────────────────────────────────────────── */
/**
 * Which of our nine metafield types a remote type can honestly fill.
 *
 * ⚠️⚠️ **ONE FUNCTION, USED BY THE WIZARD AND BY THE WRITER** (P2-20). The picker must offer
 * exactly what the apply service can fill: a target it cannot fill is a silent no-op, which is the
 * worst outcome available — nothing errors, nothing appears, and nobody can tell why.
 *
 * ⚠️ **`checkbox` is unreachable and that is a finding, not a gap.** Jira has no boolean field type
 * (a "checkbox" custom field is `array/option`); YouTrack has none either. Two independently
 * designed trackers declined to ship one, so `checkbox` stays a Forecastify-only type and no branch
 * below returns it.
 *
 * ⚠️ The order matters: the FIRST entry is what the wizard pre-selects.
 */
function metafieldTypesFor(remoteType) {
    const type = remoteType.trim().toLowerCase();
    /* ── YouTrack, whose type id is a single string with the cardinality in it ───────────────── */
    if (/^(enum|state|build|version|ownedfield|group)\[1\]$/u.test(type))
        return ['select'];
    if (/^(enum|state|build|version|ownedfield|group)\[\*\]$/u.test(type))
        return ['multi'];
    if (type === 'user[1]')
        return ['user', 'text'];
    if (type === 'user[*]')
        return ['multi'];
    if (type === 'integer' || type === 'float')
        return ['number'];
    if (type === 'string')
        return ['text', 'url'];
    if (type === 'text')
        return ['textarea'];
    if (type === 'date')
        return ['date'];
    if (type === 'date and time')
        return ['date', 'text'];
    /**
     * ⚠️⚠️ **A PERIOD IS `{minutes}`, AND IT BECOMES HOURS — NEVER THE `presentation` STRING.**
     * `"2h 30m"` is localised and unparseable; 150 minutes is 2.5, which is a number we already know
     * how to render.
     */
    if (type === 'period')
        return ['number'];
    /* ── Jira, whose `schema.type` we join with `items` as `type/items` ──────────────────────── */
    /* ⚠️ `string` and `number` are handled above — the two vocabularies genuinely agree on those two
       words, and repeating them here would be a branch that can never be reached. */
    if (type === 'number')
        return ['number'];
    if (type === 'datetime')
        return ['date', 'text'];
    if (type === 'option')
        return ['select'];
    if (type === 'option-with-child')
        return ['text'];
    if (type === 'array/option')
        return ['multi'];
    if (type === 'array/string')
        return ['multi'];
    if (type === 'array/version' || type === 'array/component' || type === 'array/group') {
        return ['multi'];
    }
    if (type === 'user')
        return ['user', 'text'];
    if (type === 'array/user')
        return ['multi'];
    /** ⚠️ Sprint and most Marketplace fields. Shape unknown at discovery: stringify, and warn. */
    if (type === 'any')
        return ['text'];
    /**
     * ⚠️ **An unknown type offers nothing, rather than guessing `text`.** A guess here writes a
     * stringified object into somebody's task and calls it data. The step lists the field, says we do
     * not know what it is, and moves on.
     */
    return [];
}
/** Whether a remote field can be mapped at all — the wizard's enabled/disabled test. */
function isMappable(field) {
    return !field.claimed && metafieldTypesFor(field.remoteType).length > 0;
}
/** ⚠️ Guards the table above against a metafield type being renamed out from under it. */
function assertMetafieldTypesAreKnown(types) {
    for (const type of types) {
        if (!metafields_1.METAFIELD_TYPES.includes(type)) {
            throw new Error(`metafieldTypesFor returned an unknown metafield type: ${type}`);
        }
    }
}
//# sourceMappingURL=remote.js.map